import React, { useContext, useRef } from "react";
import { Box, Typography } from "@material-ui/core";
import { RouteComponentProps } from "react-router";
import "./styles.scss";
import {
  B2BSpinner,
  BannerSeverity,
  ButtonWrap,
  CheckoutStepper,
  getHSPEarnOfferDescription,
  Icon,
  IconName,
  LoadingIndicator,
  NotificationBanner,
  TripPurposePanel,
  TripDescription,
  usePrevious,
  Tooltip,
} from "halifax";
import clsx from "clsx";
import {
  FlightPassengerSelectors,
  GenericChildState,
  getChildState,
  getParentState,
  LodgingSelectors,
  ParentState,
  RewardsPaymentEventTypes,
  SeatSelectors,
  useCheckoutState,
  useCheckoutStateSelector,
  WalletSelectors,
} from "@capone/checkout";
import { SeatMapResponseEnum } from "@b2bportal/air-booking-api";
import {
  CustomerAccountRole,
  PackagePricing,
  RewardsAccount,
  TravelProductEnum,
} from "redmond";
import queryStringParser from "query-string";
import { isCorpTenant, getIsEMEligible, isVesperTenant, getConfigTenant } from "@capone/common";
import { DesktopPackagesBookWorkflowConnectorProps } from "./container";
import { PATH_BOOK_CONFIRMATION, PATH_HOME } from "../../../../utils/paths";
import { ClientContext } from "../../../../App";
import {
  CHANGE_CTA_TEXT,
  CHANGE_WHO_CHECKING_IN,
  CHANGE_WHO_FLYING,
  FLIGHT_TRAVELERS_HEADING,
  HEADER_MAIN_TEXT,
  HEADER_SUB_TEXT,
  HOTEL_TRAVELERS_HEADING,
  LOADING_TEXT,
  PAYMENT_EARN_INFO_TOOLTIP_TEXT,
  PAYMENT_HEADING,
  PAYMENT_SUBHEADING,
  SEAT_SELECTION_HEADING,
  SEAT_SELECTION_NOT_AVAILABLE,
  SELECTED_PAX_DIFFERS_FROM_SEARCHED_BANNER_TEXT,
  SELECTED_SEATS_COMPLETED_TEXT,
  SKIPPED_SEATS_COMPLETED_TEXT,
} from "./textConstants";
import {
  PackagesShopProgressBar,
  PackageCheckoutDetails,
  PackagesPriceBreakdown,
  PackageTreesCard,
  PassportAwarenessCard,
} from "..";
import { Event, TEvent } from "../../state/events";
import { PackagesMachineContext } from "../../state/types";
import { PackagesContactInfo } from "../ContactInfo";
import { LodgingSelectors as PackageLodgingSelectors } from "../../state/selectors";
import { BookingInProgressModal } from "../BookingInProgressModal";
import { BookingErrorModal } from "../BookingErrorModal";
import { getCheckoutSteps, trackHotelShopRedirect } from "./utils";
import { SeatSelection } from "../SeatSelection";
import { PackagesRewardsAndPayment } from "../RewardsAndPayment";
import { ConnectedPackageDiscountBanner } from "../PackageDiscountBanner";
import { goToHotelShop } from "../../../hotel-shop/utils/queryStringHelpers";
import { useCorePackagesNavigation } from "../../../../context/CorePackagesNavigationContext";
import {
  getPackageFlightCardTotal,
  getPackageHotelCardTotal,
} from "../../state/selectors/common";
import { productToEarnV2 } from "../../../../api/v1/rewards/productToEarn";
import { CancellationPoliciesCard } from "../CancellationPoliciesCard";
import {
  AVAILABLE,
  getExperimentVariant,
  PACKAGES_CHECKOUT_OPTIMIZATIONS_V1,
  useExperiments,
  useIsPackagesShoppingApiV2Enabled,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";
import { config } from "../../../../api/config";
import { pollPackageVersion } from "../../../itinerary-review/utils/pollPackageVersion";

import { FlightBookPassengerSelection as CorpFlightBookPassengerSelection } from "../capone-corporate/FlightBookPassengerSelection";
import { FlightBookPassengerSelection as COTFlightBookPassengerSelection } from "../FlightBookPassengerSelection";
import { HotelBookPassengerSelection as CorpHotelBookPassengerSelection } from "../capone-corporate/HotelBookPassengerSelection";
import { HotelBookPassengerSelection as COTHotelBookPassengerSelection } from "../HotelBookPassengerSelection";

export interface IDesktopPackagesBookWorkflowProps
  extends RouteComponentProps,
  DesktopPackagesBookWorkflowConnectorProps { }

export type DesktopPackageBookValidationError =
  | "flight-travelers"
  | "contact-info"
  | "hotel-travelers"
  | "seats"
  | "rewards"
  | "card";

export const DesktopPackagesBookWorkflow = ({
  history,
  finalizePackageResponse,
  selectedLodging,
  selectedTrip,
  tripDetails,
  selectedRoom,
  hotelFromDate,
  hotelUntilDate,
  packagePricing,
  rewardsAccounts,
  searchedAdultsCount,
  searchedChildren,
  searchedInfants,
  searchedDestination,
  searchedDepartureDate,
  searchedReturnDate,
  searchedOrigin,
  recommendedFlights,
  opaqueQuoteRequest,
  opaquePackageToken,
  selectedAccount,
  entryPoint,
  packageDiscountInfo,
  selectedRoomRateId,
  isInternationalTrip,
  searchKey,
  selectedRoomV2,
  selectedRoomRateIdV2,
  packagePricingV2,
  packagedRoomComponentV2,
  packagePollState,
}: IDesktopPackagesBookWorkflowProps) => {
  const { logo, sessionInfo, corpBusinessAccounts, policies } =
    useContext(ClientContext);
  const [validationErrorTypes, setValidationErrorTypes] =
    React.useState<DesktopPackageBookValidationError[]>();

  const expState = useExperiments();

  const isPackageCheckoutOptimizationExperiment =
    getExperimentVariant(
      expState.experiments,
      PACKAGES_CHECKOUT_OPTIMIZATIONS_V1
    ) === AVAILABLE;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isVesper = isVesperTenant(getConfigTenant());

  const isPackagesShoppingApiV2Enabled = useIsPackagesShoppingApiV2Enabled();

  // Under v2, source room/pricing from the GetPackage snapshot (fetched by
  // Review's pollPackageVersion) instead of the v0 handoff/shop-response data.
  const effectiveSelectedRoom = isPackagesShoppingApiV2Enabled
    ? selectedRoomV2
    : selectedRoom;
  const effectiveSelectedRoomRateId = isPackagesShoppingApiV2Enabled
    ? selectedRoomRateIdV2
    : selectedRoomRateId;
  const effectivePackagePricing = isPackagesShoppingApiV2Enabled
    ? packagePricingV2
    : packagePricing;

  const isExpenseManagementExperiment =
    getExperimentVariant(expState.experiments, "corp-expense-management") ===
    AVAILABLE;

  const isEMEligible = getIsEMEligible(selectedAccount);

  const flightPAXSectionRef = useRef<HTMLDivElement>(null);
  const hotelPAXSectionRef = useRef<HTMLDivElement>(null);
  const seatSelectionSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);

  const [state, send] = useCheckoutState<TEvent, PackagesMachineContext>();
  const coreNav = useCorePackagesNavigation();

  const parentState = getParentState(state.value) as ParentState;
  const childState = getChildState(state.value);

  const selectedFlightPassengers = useCheckoutStateSelector(
    FlightPassengerSelectors.getAllSelectedUserPassengersParent
  );
  const selectedHotelPassenger = useCheckoutStateSelector(
    PackageLodgingSelectors.getSelectedUserPassengerParent
  );
  const selectedSeats = useCheckoutStateSelector(
    SeatSelectors.getSelectedSeats
  );
  const seatTotalPricing = useCheckoutStateSelector(
    SeatSelectors.getSeatTotalPricing
  );
  const skippedSeatSelection = useCheckoutStateSelector(
    SeatSelectors.getSkipSeatSelection
  );
  const seatMapAvailability = useCheckoutStateSelector(
    SeatSelectors.getSeatMapAvailability
  );
  const flightTotal = useCheckoutStateSelector(getPackageFlightCardTotal);
  const hotelTotal = useCheckoutStateSelector(getPackageHotelCardTotal);
  const offerToApply = useCheckoutStateSelector(
    WalletSelectors.getSelectedOffer
  );

  const searchedGuestCount = useCheckoutStateSelector(
    LodgingSelectors.getGuestCount
  );

  const selectedTravelerCountDiffersFromSearched =
    selectedFlightPassengers.length !== searchedGuestCount;

  const numPassengersWithSelectedSeats = React.useMemo(
    () => new Set(selectedSeats.map((seat) => seat.person_id)).size,
    [selectedSeats]
  );

  const goToFlightTravelers = () => {
    send(Event.GO_TO_PASSENGER_SELECT);
  };

  const goToHotelTravelers = () => {
    send(Event.GO_TO_HOTEL_PASSENGER_SELECT);
  };

  const goToSeatSelection = () => {
    send(Event.GO_TO_SEAT_SELECTION);
  };

  const setRewardsAccounts = (accounts: RewardsAccount[]) =>
    send({
      type: RewardsPaymentEventTypes.SET_REWARDS_ACCOUNTS,
      accounts,
    });

  const previousParentState = usePrevious(parentState);

  // Under v2, Book can be reached directly without ever passing through
  // Review (e.g. desktop's funnel skips the REVIEW step) — Review's mount
  // effect is the only other place that triggers pollPackageVersion, so if
  // nothing has populated the snapshot yet, fetch it here instead of relying
  // on Review having run first. Mirrors Review's own mount effect
  // (itinerary-review/component.tsx).
  React.useEffect(() => {
    if (!isPackagesShoppingApiV2Enabled) return;
    // Snapshot already present (e.g. the user did pass through Review first)
    // — don't double-fetch.
    if (packagedRoomComponentV2 != null) return;
    if (searchKey == null) return;

    const { packageVersionPollTarget } = queryStringParser.parse(
      window.location.search
    );
    const parsed = Number(packageVersionPollTarget);
    const versionPollTarget = Number.isInteger(parsed) ? parsed : undefined;

    pollPackageVersion({ searchKey, targetVersion: versionPollTarget });
    // Deliberately only re-runs if searchKey/the v2 flag change — re-running
    // on every packagedRoomComponentV2 change would re-fetch after the
    // poll's own dispatch resolves it.
  }, [isPackagesShoppingApiV2Enabled, searchKey]);

  React.useEffect(() => {
    switch (parentState) {
      case ParentState.passengerInformation:
        if (
          !isPackageCheckoutOptimizationExperiment ||
          previousParentState !== ParentState.loading
        ) {
          flightPAXSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        }
        break;
      case ParentState.lodgingPassengerInformation:
        hotelPAXSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case ParentState.seatSelection:
      case ParentState.cartUpdateForSeats:
        seatSelectionSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      case ParentState.cardPayment:
        paymentSectionRef.current?.scrollIntoView({ behavior: "smooth" });
        break;
      default:
        break;
    }
  }, [parentState]);

  // initialize checkout context from redux
  React.useEffect(() => {
    // Under v2, only packagedRoomComponentV2 and effectivePackagePricing are
    // required. effectiveSelectedRoom can be undefined even once a room is
    // selected (depends on the BE's shopDetails field, which isn't always
    // populated), but its only consumers are cosmetic and already optional-chain
    // it. Everything else below is v0-handoff-shaped data that's `undefined`
    // under v2 and already handled downstream (optional-chained, or tripDetails
    // guarded separately).
    const isReady = isPackagesShoppingApiV2Enabled
      ? Boolean(
          packagedRoomComponentV2 &&
            effectivePackagePricing &&
            expState.experiments.length
        )
      : Boolean(
          selectedLodging &&
            effectiveSelectedRoom &&
            selectedTrip &&
            tripDetails &&
            hotelFromDate &&
            hotelUntilDate &&
            effectivePackagePricing &&
            searchedDestination &&
            searchedOrigin &&
            searchedDepartureDate &&
            expState.experiments.length &&
            opaquePackageToken &&
            opaqueQuoteRequest
        );

    if (isReady) {
      const {
        selectedLodgingIndex,
        selectedLodgingIsTopSeller,
        selectedLodgingMealPlan,
        selectedLodgingIsSearched,
        lodgingSelectedFromMap,
      } = queryStringParser.parse(window.location.search);

      send({
        type: Event.INITIALIZE_CHECKOUT_STATE,
        payload: {
          selectedLodging: selectedLodging ?? undefined,
          // isReady (checked above) already guarantees effectivePackagePricing
          // is non-null; effectiveSelectedRoom can genuinely still be
          // undefined under v2 (shopDetails gap) — selectedRoom is optional
          // on the payload type for exactly this reason.
          selectedRoom: effectiveSelectedRoom ?? undefined,
          selectedTrip: selectedTrip ?? undefined,
          selectedRoomRateId: effectiveSelectedRoomRateId,
          finalizePackageResponse,
          tripDetails: tripDetails ?? undefined,
          hotelFromDate,
          hotelUntilDate,
          packagePricing: effectivePackagePricing as PackagePricing,
          searchedAdultsCount,
          searchedChildren,
          searchedInfants: searchedInfants.map((infant) => infant.age),
          searchedDestination: searchedDestination ?? undefined,
          searchedDepartureDate: searchedDepartureDate ?? undefined,
          searchedReturnDate,
          searchedOrigin: searchedOrigin ?? undefined,
          benchmarkSliceIds: recommendedFlights ?? [],
          selectedLodgingIndex:
            typeof selectedLodgingIndex === "string"
              ? parseInt(selectedLodgingIndex, 10)
              : undefined,
          selectedAccount,
          entryPoint,
          discountInfo: packageDiscountInfo,
          experiments: expState.experiments,
          opaqueFlightRequest: opaquePackageToken?.value,
          opaqueLodgingRequest: opaqueQuoteRequest,
          searchKey,
          sessionInfo,
          corpBusinessAccounts,
          policyTier: policies,
          channel: "Web",
          selectedLodgingIsTopSeller: selectedLodgingIsTopSeller === "true",
          selectedLodgingMealPlan:
            typeof selectedLodgingMealPlan === "string"
              ? selectedLodgingMealPlan
              : undefined,
          selectedLodgingIsSearched: selectedLodgingIsSearched === "true",
          lodgingSelectedFromMap: lodgingSelectedFromMap === "true",
        },
      });
      return;
    }

    // Under v2, "not ready yet" might just mean the snapshot fetch (triggered
    // above, or by Review if it ran first) hasn't resolved — don't redirect
    // until the poll has genuinely settled with nothing to show. Under v0
    // there's no equivalent async signal, so fall through to redirect
    // immediately, same as before.
    if (isPackagesShoppingApiV2Enabled && packagePollState !== "settled") {
      return;
    }

    trackHotelShopRedirect(
      opaqueQuoteRequest,
      opaquePackageToken,
      finalizePackageResponse,
      {
        selectedLodging,
        selectedRoom: effectiveSelectedRoom,
        selectedTrip,
        tripDetails,
        hotelFromDate,
        hotelUntilDate,
        packagePricing: effectivePackagePricing,
        searchedDestination,
        searchedOrigin,
        searchedDepartureDate,
      }
    );
    if (coreNav.goToHotelShop != null) {
      coreNav.goToHotelShop();
    } else {
      goToHotelShop({ history });
    }
  }, [
    selectedLodging,
    effectiveSelectedRoom,
    selectedTrip,
    finalizePackageResponse,
    tripDetails,
    hotelFromDate,
    hotelUntilDate,
    effectivePackagePricing,
    searchedAdultsCount,
    searchedChildren,
    searchedInfants,
    searchedDestination,
    searchedDepartureDate,
    searchedReturnDate,
    searchedOrigin,
    recommendedFlights,
    expState.experiments,
    packagePollState,
  ]);

  const FlightBookPassengerSelection = isCorpTenant(config.TENANT)
    ? CorpFlightBookPassengerSelection
    : COTFlightBookPassengerSelection;
  const HotelBookPassengerSelection = isCorpTenant(config.TENANT)
    ? CorpHotelBookPassengerSelection
    : COTHotelBookPassengerSelection;

  React.useEffect(() => {
    // update checkout state with rewards accounts from redux
    setRewardsAccounts(rewardsAccounts);
  }, [rewardsAccounts]);

  React.useEffect(() => {
    // update path so header becomes visible for confirmation
    if (parentState === ParentState.bookingConfirmation) {
      history.replace(PATH_BOOK_CONFIRMATION);
    }
  }, [parentState]);

  React.useEffect(() => {
    send({ type: Event.SET_PLATFORM, platform: "desktop" });
  }, []);

  React.useEffect(() => {
    if (flightTotal !== undefined && hotelTotal !== undefined) {
      Promise.all(
        rewardsAccounts.map((account) =>
          productToEarnV2({
            account: account.accountReferenceId,
            earnItems: {
              [TravelProductEnum.Flights]: {
                amountUsd: flightTotal,
              },
              [TravelProductEnum.Hotels]: {
                amountUsd: hotelTotal,
                earnOfferDescription: getHSPEarnOfferDescription(offerToApply),
              },
            },
          })
            .then((res) => {
              send({
                type: RewardsPaymentEventTypes.SET_EARN_BY_REWARDS_ACCOUNT_ID,
                accountReferenceId: account.accountReferenceId,
                earnResponse: res,
              });
            })
            .catch((e) =>
              console.warn(
                `Failed to fetch earn for account ${account.accountReferenceId}: ${e}`
              )
            )
        )
      );
    }
  }, [flightTotal, hotelTotal, offerToApply]);

  return (
    <Box
      className={clsx("desktop-packages-book-workflow-root", {
        xmod: isXmodRedesign,
        redesigned: isVesper,
      })}
    >
      <CheckoutStepper
        steps={getCheckoutSteps(parentState)}
        headerCopy={HEADER_MAIN_TEXT}
        subHeaderCopy={HEADER_SUB_TEXT}
        logo={
          <ButtonWrap
            className="logo"
            onClick={() => {
              history.push(PATH_HOME);
            }}
          >
            {logo}
          </ButtonWrap>
        }
        className={clsx("b2b", "combined-step")}
      />

      <Box className="packages-book-progress-bar-wrapper">
        <PackagesShopProgressBar />
      </Box>

      <Box className="packages-book-body-root">
        <Box className="packages-book-body-wrapper">
          <Box className="packages-book-body-left">
            {isCorpTenant(config.TENANT) && !isExpenseManagementExperiment && (
              <Box className={clsx("packages-book-section", "trip-purpose")}>
                <TripPurposePanel
                  setTripPurpose={(tripPurpose) => {
                    send({ type: Event.SET_TRIP_PURPOSE, tripPurpose });
                  }}
                />
              </Box>
            )}

            <PackageCheckoutDetails />

            {isInternationalTrip && (
              <Box className="packages-book-section">
                <PassportAwarenessCard />
              </Box>
            )}

            <div
              className={clsx("packages-book-section", {
                selected: [
                  ParentState.passengerInformation,
                  ParentState.passport,
                  ParentState.contactInformation,
                ].includes(parentState),
              })}
              ref={flightPAXSectionRef}
            >
              <Box className="packages-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="packages-book-section-heading"
                >
                  {FLIGHT_TRAVELERS_HEADING}
                </Typography>

                {(() => {
                  switch (true) {
                    case [
                      "waitForState",
                      "finalizePackage",
                      ParentState.loading,
                    ].includes(parentState):
                      return (
                        <LoadingIndicator
                          className="packages-checkout-loading-indicator"
                          indicatorSize="small"
                          indicator={B2BSpinner}
                          message={LOADING_TEXT}
                        />
                      );
                    case [
                      ParentState.passengerInformation,
                      ParentState.passport,
                      ParentState.contactInformation,
                    ].includes(parentState):
                      return (
                        <>
                          <hr />

                          <FlightBookPassengerSelection
                            validationErrorTypes={validationErrorTypes}
                          />

                          <hr />

                          <PackagesContactInfo
                            validationErrorTypes={validationErrorTypes}
                            setValidationErrorTypes={setValidationErrorTypes}
                          />
                        </>
                      );
                    case !!selectedFlightPassengers.length:
                      return (
                        <Box className="packages-book-filled-section">
                          <Icon name={IconName.User} />
                          <Typography className="packages-book-filled-section-content">
                            {selectedFlightPassengers.reduce(
                              (nameString, person, i) => {
                                const firstLastName = `${person.givenName} ${person.surname}`;
                                if (i > 0)
                                  return `${nameString}, ${firstLastName}`;

                                return firstLastName;
                              },
                              ""
                            )}
                          </Typography>
                        </Box>
                      );
                    default:
                      return null;
                  }
                })()}
              </Box>
              {![
                ParentState.passengerInformation,
                ParentState.passport,
                ParentState.contactInformation,
              ].includes(parentState) &&
                !!selectedFlightPassengers.length && (
                  <Box className="packages-book-section-change-cta-wrapper">
                    <ButtonWrap onClick={goToFlightTravelers} aria-label={CHANGE_WHO_FLYING}>
                      <Typography>{CHANGE_CTA_TEXT}</Typography>
                    </ButtonWrap>
                  </Box>
                )}
            </div>

            <div
              className={clsx("packages-book-section", {
                selected: [ParentState.lodgingPassengerInformation].includes(
                  parentState
                ),
              })}
              ref={hotelPAXSectionRef}
            >
              <Box className="packages-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="packages-book-section-heading"
                >
                  {HOTEL_TRAVELERS_HEADING}
                </Typography>

                {(() => {
                  switch (true) {
                    case parentState ===
                      ParentState.lodgingPassengerInformation:
                      return (
                        <>
                          {selectedTravelerCountDiffersFromSearched ? (
                            <NotificationBanner
                              className="searched-pax-count-mismatch-banner"
                              severity={BannerSeverity.WARNING}
                              content={
                                <>
                                  {SELECTED_PAX_DIFFERS_FROM_SEARCHED_BANNER_TEXT(
                                    selectedFlightPassengers.length <
                                    searchedGuestCount
                                  )}
                                </>
                              }
                            />
                          ) : null}

                          <hr />
                          <HotelBookPassengerSelection
                            validationErrorTypes={validationErrorTypes}
                            setValidationErrorTypes={setValidationErrorTypes}
                          />
                        </>
                      );
                    case !!selectedHotelPassenger:
                      return (
                        <Box className="packages-book-filled-section">
                          <Icon name={IconName.User} />
                          <Typography className="packages-book-filled-section-content">
                            {selectedHotelPassenger!.givenName}{" "}
                            {selectedHotelPassenger!.surname}
                          </Typography>
                        </Box>
                      );
                    default:
                      return null;
                  }
                })()}
              </Box>
              {![ParentState.lodgingPassengerInformation].includes(
                parentState
              ) &&
                selectedHotelPassenger && (
                  <Box className="packages-book-section-change-cta-wrapper">
                    <ButtonWrap onClick={goToHotelTravelers} aria-label={CHANGE_WHO_CHECKING_IN}>
                      <Typography>{CHANGE_CTA_TEXT}</Typography>
                    </ButtonWrap>
                  </Box>
                )}
            </div>

            <div
              className={clsx("packages-book-section", {
                selected: [ParentState.seatSelection].includes(parentState),
              })}
              ref={seatSelectionSectionRef}
            >
              <Box className="packages-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="packages-book-section-heading"
                >
                  {SEAT_SELECTION_HEADING}
                </Typography>

                {((parentState === ParentState.seatSelection &&
                  childState === GenericChildState.loading) ||
                  parentState === ParentState.cartUpdateForSeats) && (
                    <LoadingIndicator
                      className="packages-checkout-loading-indicator"
                      indicatorSize="small"
                      indicator={B2BSpinner}
                      message={LOADING_TEXT}
                    />
                  )}

                {(() => {
                  switch (true) {
                    case parentState === ParentState.seatSelection &&
                      childState !== GenericChildState.loading:
                      return (
                        <>
                          <hr />
                          <SeatSelection
                            validationErrorTypes={validationErrorTypes}
                            setValidationErrorTypes={setValidationErrorTypes}
                          />
                        </>
                      );
                    case selectedSeats.length ||
                      skippedSeatSelection ||
                      seatMapAvailability ===
                      SeatMapResponseEnum.SeatMapUnavailable:
                      return (
                        <Box className="packages-book-filled-section">
                          {!skippedSeatSelection &&
                            seatMapAvailability ===
                            SeatMapResponseEnum.SeatMapAvailable && (
                              <Icon name={IconName.User} />
                            )}
                          <Typography className="packages-book-filled-section-content">
                            {(() => {
                              switch (true) {
                                case skippedSeatSelection:
                                  return SKIPPED_SEATS_COMPLETED_TEXT;
                                case !!selectedSeats.length:
                                  return SELECTED_SEATS_COMPLETED_TEXT(
                                    numPassengersWithSelectedSeats,
                                    seatTotalPricing
                                  );
                                case seatMapAvailability ===
                                  SeatMapResponseEnum.SeatMapUnavailable:
                                default:
                                  return SEAT_SELECTION_NOT_AVAILABLE;
                              }
                            })()}
                          </Typography>
                        </Box>
                      );
                    default:
                      return null;
                  }
                })()}
              </Box>
              {![ParentState.seatSelection].includes(parentState) &&
                (selectedSeats.length || skippedSeatSelection) && (
                  <Box className="packages-book-section-change-cta-wrapper">
                    <ButtonWrap onClick={goToSeatSelection}>
                      <Typography>{CHANGE_CTA_TEXT}</Typography>
                    </ButtonWrap>
                  </Box>
                )}
            </div>

            <div
              className={clsx("packages-book-section", {
                selected: [
                  ParentState.wallet,
                  ParentState.rewardsPayment,
                  ParentState.cardPayment,
                ].includes(parentState),
              })}
              ref={paymentSectionRef}
            >
              <Box className="packages-book-section-main-content-wrapper">
                <Typography
                  variant="h3"
                  className="packages-book-section-heading"
                >
                  {PAYMENT_HEADING(
                    isCorpTenant(config.TENANT) &&
                    selectedAccount?.customerAccountRole ===
                    CustomerAccountRole.Primary
                  )}
                </Typography>

                {parentState === ParentState.cartUpdate && (
                  <LoadingIndicator
                    className="packages-checkout-loading-indicator"
                    indicatorSize="small"
                    indicator={B2BSpinner}
                    message={LOADING_TEXT}
                  />
                )}

                {(() => {
                  switch (true) {
                    case [
                      ParentState.wallet,
                      ParentState.rewardsPayment,
                      ParentState.cardPayment,
                    ].includes(parentState):
                      return (
                        <>
                          <Box className="packages-book-section-subheading-wrapper">
                            <Typography
                              variant="h4"
                              className="packages-book-section-subheading"
                            >
                              {PAYMENT_SUBHEADING}
                            </Typography>
                            <Tooltip
                              content={
                                <Typography className="payment-earn-info-popover-content">
                                  {PAYMENT_EARN_INFO_TOOLTIP_TEXT}
                                </Typography>
                              }
                              triggerClassName="payment-earn-info-btn"
                              title="Rewards & Payment"
                            >
                              <Icon name={IconName.InfoCircle} />
                            </Tooltip>
                          </Box>
                          <hr />
                          <PackagesRewardsAndPayment
                            validationErrorTypes={validationErrorTypes}
                            setValidationErrorTypes={setValidationErrorTypes}
                          />
                        </>
                      );
                    default:
                      return null;
                  }
                })()}
              </Box>
            </div>
            {isPackageCheckoutOptimizationExperiment ? undefined : (
              <CancellationPoliciesCard />
            )}

            {isCorpTenant(config.TENANT) && isExpenseManagementExperiment && (
              <Box className={clsx("packages-book-section", "trip-purpose")}>
                <TripDescription
                  setTripDescription={(tripDescription) => {
                    send({ type: Event.SET_TRIP_DESCRIPTION, tripDescription });
                  }}
                  isEMEligible={isEMEligible}
                />
              </Box>
            )}

            <PackageTreesCard />
          </Box>
          <Box className="packages-book-body-right">
            <Box className="packages-book-body-right-sticky-container">
              <PackagesPriceBreakdown
                setValidationErrorTypes={setValidationErrorTypes}
              />
              <ConnectedPackageDiscountBanner
                isPackageCheckoutOptimizationExperiment={
                  isPackageCheckoutOptimizationExperiment
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <BookingInProgressModal />
      <BookingErrorModal />
    </Box>
  );
};
